import React, { useCallback } from 'react';
import { Typography, TypographyType, TypographyVariant } from 'src/components-dummy';
import { GridLayoutSectionStyled } from 'src/components-bl/Galleries/CustomInspirations/EditCustomInspirationsGalleryPage/components/EditGallerySideBar/components/GridLayoutSection/GridLayoutSection.styles';
import { LayoutRowStyled, WidthWrapperStyled } from './LayoutSection.styles';
import { SliderRow } from 'src/components-dummy/SliderRow';
import { CampaignDraft } from 'src/components-bl/EmailMarketing/types/campaign-draft';
import { WidgetMaker } from 'src/components-bl/EmailMarketing/types/widget-maker';
import { ErrorLabelStyled } from '../EmailDisplaySettingsSection/EmailDisplaySettingsSection.styles';
import { ColorStyle } from '../EmailDisplaySettingsSection/ColorStyle';
import { TextField } from '@mui/material';

interface LayoutSectionProps {
  widgetMaker: Partial<WidgetMaker>;
  onChange: (campaign: CampaignDraft) => void;
  getErrorMessage: (key?: string) => string;
  isMobile: boolean;
}

export const LayoutSection = ({
  widgetMaker,
  onChange,
  getErrorMessage,
  isMobile,
}: LayoutSectionProps): JSX.Element => {
  const widthValue = isMobile ? 360 : widgetMaker.width;

  const handleNumberOfRowsChange = useCallback(
    (newNumberOfRows: number) => {
      onChange({
        widgetMaker: {
          ...widgetMaker,
          numberOfRows: newNumberOfRows,
        },
      });
    },
    [onChange, widgetMaker]
  );

  const handleItemsPerRowChange = useCallback(
    (newItemsPerRow: number) => {
      onChange({
        widgetMaker: {
          ...widgetMaker,
          itemsPerRow: newItemsPerRow,
        },
      });
    },
    [onChange, widgetMaker]
  );

  const handleWidthChange = useCallback(
    e => {
      const newWidth = Number(e.target.value);
      onChange({
        widgetMaker: {
          ...widgetMaker,
          width: newWidth,
        },
      });
    },
    [onChange, widgetMaker]
  );

  const handleBgColorChange = useCallback(
    (field, value) => {
      onChange({
        widgetMaker: {
          ...widgetMaker,
          [field]: value,
        },
      });
    },
    [onChange, widgetMaker]
  );

  const errorValueMessage = getErrorMessage();

  return (
    <GridLayoutSectionStyled>
      <WidthWrapperStyled>
        <Typography type={TypographyType.Paragraph} variant={TypographyVariant.MediumRegular}>
          Widget Width
        </Typography>
        <TextField
          type='number'
          onChange={handleWidthChange}
          value={widthValue || undefined}
          InputProps={{
            endAdornment: <span style={{ fontFamily: 'monospace' }}>px</span>,
          }}
          error={!!errorValueMessage}
          disabled={isMobile}
        />
        {errorValueMessage && <ErrorLabelStyled>{errorValueMessage}</ErrorLabelStyled>}
        <ColorStyle
          field='textBackgroundColor'
          label='Widget Background color'
          defaultColor='#ffffff'
          color={widgetMaker?.textBackgroundColor}
          onChange={handleBgColorChange}
          isLayout
        />
      </WidthWrapperStyled>
      <Typography type={TypographyType.Paragraph} variant={TypographyVariant.MediumRegular}>
        Number of rows
      </Typography>
      <SliderRow
        key='numberOfRows'
        value={widgetMaker.numberOfRows}
        onChange={handleNumberOfRowsChange}
        min={1}
        max={4}
      />
      <LayoutRowStyled>
        <Typography type={TypographyType.Paragraph} variant={TypographyVariant.MediumRegular}>
          Number of columns
        </Typography>
        <SliderRow
          key='itemsPerRow'
          value={widgetMaker.itemsPerRow}
          onChange={handleItemsPerRowChange}
          min={1}
          max={4}
        />
      </LayoutRowStyled>
    </GridLayoutSectionStyled>
  );
};
